import { useEffect, useState } from "react";
import {
  portfolio as api,
  type InvestorPortfolio as Portfolio,
} from "../../../api";
import { Link, useRouter } from "../../../router";
import { formatCurrency } from "../../portfolio/format";

// Compact portfolio preview on the client card: headline value plus the
// account list. Deep analysis (positions, returns) stays in the Portfolio
// section — every row links across.
export function InvestorPortfolioTab({ investorId }: { investorId: number }) {
  const { navigate } = useRouter();
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .forInvestor(investorId)
      .then((d) => !cancelled && setPortfolio(d.portfolio))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [investorId]);

  if (error) return <div className="banner-error">{error}</div>;
  if (!portfolio) return <div className="muted">Loading…</div>;

  if (portfolio.accounts.length === 0) {
    return (
      <div className="empty">
        <p>No accounts yet.</p>
        <Link to={`/app/portfolio/${investorId}`} className="btn btn-ghost">
          Open in Portfolio
        </Link>
      </div>
    );
  }

  const currencies = Object.keys(portfolio.by_currency);

  return (
    <div>
      <section className="aggregate">
        <div className="aggregate-figure">
          <span className="label">Total value</span>
          <span className="value">
            {currencies
              .map((c) => formatCurrency(portfolio.by_currency[c]!, c))
              .join(" + ")}
          </span>
        </div>
        <div className="aggregate-meta">
          <span>
            <strong>{portfolio.accounts.length}</strong>{" "}
            {portfolio.accounts.length === 1 ? "account" : "accounts"}
          </span>
          <Link to={`/app/portfolio/${investorId}`} className="link-action">
            Open in Portfolio →
          </Link>
        </div>
      </section>

      <table className="table">
        <thead>
          <tr>
            <th>Account</th>
            <th>Currency</th>
            <th className="num">Value</th>
          </tr>
        </thead>
        <tbody>
          {portfolio.accounts.map((a) => (
            <tr
              key={a.id}
              className="row-link"
              onClick={() => navigate(`/app/portfolio/accounts/${a.id}`)}
            >
              <td className="strong">{a.name}</td>
              <td>{a.currency}</td>
              <td className="num">{formatCurrency(a.value, a.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
